import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LayoutGrid, ChevronDown, Check, Home, Hammer, Radar, Lock } from "lucide-react";
import { useArcaOSAccess } from "./useArcaOSAccess";

// Scalable ecosystem Workspace Switcher.
// Architecture: ArcaCentum (Company) -> ArcaOS (Operating System) -> Mission Control
// (Workspace) -> Executive Dashboard (Home). Every future product plugs in here as a
// new workspace, so the whole ecosystem feels like ONE platform, not separate apps.
const PRODUCT_WORKSPACES = [
  { key: "arcacapitalis", label: "ArcaCapitalis", desc: "Capital & funding" },
  { key: "arcacredit", label: "ArcaCredit", desc: "Business credit" },
  { key: "arcacommerce", label: "ArcaCommerce", desc: "Commerce" },
];

function currentWorkspace(pathname) {
  if (pathname.startsWith("/os")) return "mission-control";
  if (pathname.startsWith("/studio")) return "studio";
  return "marketing";
}

export default function WorkspaceSwitcher({ className = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { hasAccess } = useArcaOSAccess();
  const current = currentWorkspace(location.pathname);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const LABELS = { "mission-control": "Mission Control", studio: "Studio", marketing: "ArcaCentum.ai" };
  const go = (path) => { setOpen(false); navigate(path); };

  const primary = [
    { key: "marketing", label: "ArcaCentum.ai", desc: "Public site & home", icon: Home, path: "/", enabled: true },
    { key: "studio", label: "Studio", desc: "AI app builder", icon: Hammer, path: "/studio", enabled: true },
    { key: "mission-control", label: "Mission Control", desc: "Executive operating system", icon: Radar, path: "/os", enabled: hasAccess },
  ];

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-2 transition-colors duration-200"
        data-testid="workspace-switcher-toggle"
        aria-label="Switch workspace"
      >
        <LayoutGrid size={15} className="text-[#00F0FF]" />
        <span className="text-sm font-medium text-white hidden sm:inline">{LABELS[current] || "ArcaCentum"}</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div style={{ backgroundColor: "#0a0a0c" }} className="absolute left-0 rtl:left-auto rtl:right-0 mt-2 w-72 rounded-2xl border border-white/10 shadow-2xl shadow-black/60 p-2 z-[70]" data-testid="workspace-switcher-menu">
          <p className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-widest text-slate-500">Workspaces</p>
          {primary.map((w) => (
            <button
              key={w.key}
              onClick={() => w.enabled && go(w.path)}
              disabled={!w.enabled}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors duration-200 ${
                current === w.key ? "bg-[#0055FF]/20" : w.enabled ? "hover:bg-white/5" : "opacity-40 cursor-not-allowed"}`}
              data-testid={`workspace-${w.key}`}
            >
              <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><w.icon size={15} className="text-[#00F0FF]" /></span>
              <span className="flex-1 leading-tight">
                <span className="block text-sm font-medium text-white">{w.label}</span>
                <span className="block text-[11px] text-slate-500">{w.enabled ? w.desc : "Requires ArcaOS access"}</span>
              </span>
              {current === w.key && <Check size={15} className="text-[#00F0FF] shrink-0" />}
            </button>
          ))}
          <div className="my-2 border-t border-white/10" />
          <p className="px-3 pt-1 pb-1 text-[10px] font-mono uppercase tracking-widest text-slate-500">Product workspaces</p>
          {PRODUCT_WORKSPACES.map((w) => (
            <div key={w.key} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl opacity-50" data-testid={`workspace-soon-${w.key}`}>
              <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Lock size={13} className="text-slate-500" /></span>
              <span className="flex-1 leading-tight">
                <span className="block text-sm font-medium text-slate-300">{w.label}</span>
                <span className="block text-[11px] text-slate-600">{w.desc}</span>
              </span>
              <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500 border border-white/10 rounded-full px-2 py-0.5">Soon</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
